import { SizeConfigure } from '@lighthouse/core'
import { type FlowLayoutNode, PAGE_SCROLL_PARENT_CONTENT } from '@lighthouse/shared'
import { findParentScroller } from '@lighthouse/tools'
import { useLayoutEffect, useState } from 'react'

export function setDeadCopyNode(nodes: FlowLayoutNode[], isAlive?: boolean, realParentId?: string): FlowLayoutNode[] {
    return nodes.map(item => {
        return {
            ...item,
            static: !isAlive,
            disabled: !isAlive,
            realParentId,
            children: /** item.type === 'block' ? undefined : */ setDeadCopyNode(item.children ?? [], isAlive)
        }
    })
}

export function geVerticalWidthSize(isFitArrangement: boolean, itemWidth: number): SizeConfigure {
    if (isFitArrangement) {
        return {
            size: 'auto'
        }
    }
    if (itemWidth) {
        return {
            size: itemWidth,
            unit: 'px'
        }
    }
    return {
        size: 'fill'
    }
}

function getMinHeightConfigure(node: FlowLayoutNode): { minHeight?: SizeConfigure } {
    if (node.children && node.children.length > 0) {
        return {}
    }
    return {
        minHeight: {
            size: 80,
            unit: 'px'
        }
    }
}

export function buildVerticalItemNodeData(params: {
    node: FlowLayoutNode
    blockId: string
    recordId: string
    index: number
    readonly?: boolean
    widthSize: SizeConfigure
}): FlowLayoutNode {
    const { node, blockId, recordId, index, readonly, widthSize } = params
    const minHeightConfigure = getMinHeightConfigure(node)
    return {
        id: `${blockId}/${recordId}`,
        scope: node.scope,
        type: 'container',
        disabled: readonly || index !== 0,
        virtual: true,
        data: node.data && {
            design: node.data.design,
            size: {
                width: widthSize,
                height: {
                    size: 'auto'
                },
                ...minHeightConfigure
            },
            layout: node.data.layout,
            position: node.data.position
        },
        children: readonly ? node.children : setDeadCopyNode(node.children || [], index === 0, blockId)
    }
}

function getPageScrollElement(): HTMLElement | null {
    const pageScrollElement = document.getElementById(PAGE_SCROLL_PARENT_CONTENT)
    if (pageScrollElement) {
        return pageScrollElement
    }
    return document.scrollingElement as HTMLElement | null
}

function isUsableScrollElement(element: HTMLElement | null): element is HTMLElement {
    if (!element) {
        return false
    }
    if (element.clientHeight <= 0) {
        return false
    }
    return element.scrollHeight > element.clientHeight
}

export function useStableScrollElement(targetRef: React.RefObject<HTMLDivElement>): HTMLElement | null {
    const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null)

    useLayoutEffect(() => {
        const el = targetRef.current
        if (!el) return
        const detect = () => {
            const parent = findParentScroller(el)
            setScrollElement(isUsableScrollElement(parent) ? parent : getPageScrollElement())
        }
        detect()
        // 如果 mount 時元素在隱藏容器中（如 bottom sheet），尺寸為 0，
        // 上面的 detect() 結果不可靠。用 ResizeObserver 等到元素真正有尺寸後重新探測。
        const observer = new ResizeObserver(() => {
            if (el.clientHeight > 0) {
                detect()
                observer.disconnect()
            }
        })
        observer.observe(el)
        return () => observer.disconnect()
    }, [targetRef])

    return scrollElement
}
